<template>
  <div class="bg-gray-100">
    <div v-if="!scan">
      <page-header :heading="'Loading scan...'" />
      <div class="text-center py-12">
        <svg
          class="inline-block animate-spin -ml-1 mr-1 h-5 w-5 text-gray-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        Loading
      </div>
    </div>
    <div class="scan" v-if="scan">
      <page-header :heading="scan.name" :subheading="scanCreated" />
      <div
        class="max-w-6xl mx-auto px-8 py-8 -mt-12 bg-white rounded-lg shadow-md md:flex"
      >
        <div class="md:w-6/12 md:pr-6">
          <img :src="scan.url" class="w-full rounded shadow" v-if="scan.url" />
          <div v-if="scan.explanation" class="text-sm mt-4">
            <vue-markdown
              id="explanation"
              :source="scan.explanation"
              :options="{ breaks: true }"
              v-if="scan.explanation != 'Pending'"
            />
            <div v-else>
              <p class="text-sm text-gray-500 mt-4 mb-4">
                We're still analysing your scan. A full explanation will be
                available in a few moments.
              </p>
              <p class="text-sm font-bold">
                Refresh the page to see the full explanation.
              </p>
            </div>
          </div>
        </div>
        <div class="md:w-6/12">
          <div class="border-b-2 border-gray-100 flex flex-wrap mb-4 pb-4">
            <div class="w-5/12">
              <vc-donut
                class="text-xs"
                background="white"
                foreground="grey"
                :size="100"
                unit="px"
                :thickness="20"
                has-legend
                legend-placement="bottom"
                :sections="flowSections()"
                :total="100"
                :start-angle="0"
                :auto-adjust-text-size="true"
              >
                <h1 class="font-bold text-xs">
                  {{
                    this.niceScore("flow") > this.niceScore("balance")
                      ? "Flow"
                      : "Balance"
                  }}
                </h1>
              </vc-donut>
            </div>
            <div class="w-7/12 pl-2">
              <h3 class="font-bold text-lg">Flow vs Balance</h3>
              <p class="text-sm">
                This design has a score of {{ niceScore("flow") }} for flow and
                {{ niceScore("balance") }} for balance. This means the page /
                design {{ explainFlow() }}
              </p>
            </div>
          </div>

          <div class="border-b-2 border-gray-100 flex flex-wrap mb-4 pb-4">
            <div class="w-5/12">
              <vc-donut
                class="text-xs"
                background="white"
                foreground="grey"
                :size="100"
                unit="px"
                :thickness="20"
                has-legend
                legend-placement="bottom"
                :sections="impactSections()"
                :total="100"
                :start-angle="0"
                :auto-adjust-text-size="true"
              >
                <h1 class="font-bold text-xs">
                  {{
                    this.niceScore("impact") > this.niceScore("subtlety")
                      ? "Impact"
                      : "Subtlety"
                  }}
                </h1>
              </vc-donut>
            </div>
            <div class="w-7/12 pl-2">
              <h3 class="font-bold text-lg">Impact vs Subtlety</h3>
              <p class="text-sm">
                This design has a score of {{ niceScore("impact") }} for impact
                and {{ niceScore("subtlety") }} for subtlety. This means the
                page / design {{ explainImpact() }}
              </p>
            </div>
          </div>

          <div class="border-b-2 border-gray-100 flex flex-wrap mb-4 pb-4">
            <div class="w-5/12">
              <vc-donut
                class="text-xs"
                background="white"
                foreground="grey"
                :size="100"
                unit="px"
                :thickness="20"
                has-legend
                legend-placement="bottom"
                :sections="simplicitySections()"
                :total="100"
                :start-angle="0"
                :auto-adjust-text-size="true"
              >
                <h1 class="font-bold text-xs">
                  {{
                    this.niceScore("simplicity") > this.niceScore("information")
                      ? "Simplicity"
                      : "Information"
                  }}
                </h1>
              </vc-donut>
            </div>
            <div class="w-7/12 pl-2">
              <h3 class="font-bold text-lg">Simplicity vs Information</h3>
              <p class="text-sm">
                This design has a score of {{ niceScore("simplicity") }} for
                simplicity and {{ niceScore("information") }} for information.
                This means the page / design {{ explainSimplicity() }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        class="max-w-6xl mx-auto px-8 py-8 mt-6 mb-12 bg-white rounded-lg shadow-md"
      >
        <select class="md:w-1/4 mx-auto" name="" v-model="compare">
          <option :value="null">Choose a scan to compare</option>
          <option v-for="scan in allScans" :value="scan" :key="scan.id">{{
            scan.name
          }}</option>
        </select>

        <table class="table-auto w-full mt-6 p-1 text-sm" v-if="compare">
          <thead class="bg-gray-100">
            <tr class="text-left">
              <th>Scan</th>
              <th>Flow</th>
              <th>Balance</th>
              <th>Impact</th>
              <th>Subtlety</th>
              <th>Simplicity</th>
              <th>Information</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ scan.name }}</td>
              <td>{{ modifyAndRound(scan.flow * 1) }}</td>
              <td>{{ 100 - modifyAndRound(scan.flow * 1) }}</td>
              <td>{{ modifyAndRound(scan.impact * 1) }}</td>
              <td>{{ 100 - modifyAndRound(scan.impact * 1) }}</td>
              <td>{{ modifyAndRound(scan.simplicity * 1) }}</td>
              <td>{{ 100 - modifyAndRound(scan.simplicity * 1) }}</td>
            </tr>
            <tr>
              <td>{{ compare.name }}</td>
              <td>{{ modifyAndRound(compare.flow * 1) }}</td>
              <td>{{ 100 - modifyAndRound(compare.flow * 1) }}</td>
              <td>{{ modifyAndRound(compare.impact * 1) }}</td>
              <td>{{ 100 - modifyAndRound(compare.impact * 1) }}</td>
              <td>{{ modifyAndRound(compare.simplicity * 1) }}</td>
              <td>{{ 100 - modifyAndRound(compare.simplicity * 1) }}</td>
            </tr>
          </tbody>
        </table>

        <div class="mt-8 flex flex-wrap" v-if="compare">
          <div class="w-1/2 px-2">
            <img class="w-full rounded shadow-lg" :src="scan.url" />
          </div>
          <div class="w-1/2 px-2">
            <img class="w-full rounded shadow-lg" :src="compare.url" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script type="text/javascript">
import moment from "moment";
import { Auth, API, Storage, Analytics } from "aws-amplify";
import PageHeader from "@/components/PageHeader";
import VueMarkdown from "vue-markdown-render";

export default {
  name: "Scan",
  components: {
    PageHeader,
    VueMarkdown,
  },
  data() {
    return {
      scan: null,
      allScans: [],
      compare: null,
    };
  },
  computed: {
    scanCreated() {
      return moment(this.scan.createdAt).fromNow();
    },
  },
  methods: {
    async fetchItem() {
      const user = await Auth.currentAuthenticatedUser();
      const token = user.signInUserSession.idToken.jwtToken;

      API.get("scans", `/scans/${this.$route.params.id}`, {
        headers: {
          Authorization: token,
        },
      })
        .then((result) => {
          this.scan = JSON.parse(result.body);
        })
        .catch((err) => {
          console.log(err);
        });
    },
    async fetchData() {
      this.loading = true;
      const user = await Auth.currentAuthenticatedUser();
      const token = user.signInUserSession.idToken.jwtToken;

      const request = {
        body: {
          attr: "value",
        },
        headers: {
          Authorization: token,
        },
      };

      API.get("scans", "/scans", request)
        .then((result) => {
          this.allScans = result;
          this.loading = false;
        })
        .catch((err) => {
          console.log(err);
          this.loading = false;
        });
    },
    explainFlow() {
      if (this.scan.flow < -4) {
        return "balances a lot of different areas of content.";
      }
      if (this.scan.flow < -2.5) {
        return "provides a degree of balance between the elements of the page content";
      }
      if (this.scan.flow < 0) {
        return "strikes a balance between leading the user through the page and drawing their attention to different content";
      }
      if (this.scan.flow > 4) {
        return "guides the user through a path very clearly";
      }
      if (this.scan.flow > 2.5) {
        return "tends to guide the user through a viewing path well";
      }
      if (this.scan.flow > 0) {
        return "somewhat guides the user through the page but could be improved.";
      }
    },
    explainImpact() {
      if (this.scan.impact < -4) {
        return "is very subtle and is likely to be described as clean and subtle looking.";
      }
      if (this.scan.impact < -2.5) {
        return "is fairly subtle, maintaining a clean, uncluttered look.";
      }
      if (this.scan.impact < 0) {
        return "has elements of subtlety and impact. You may want to tweak the design to move more towards one or the other";
      }
      if (this.scan.impact > 4) {
        return "has a very high impact. Certain elements will dominate the users attention.";
      }
      if (this.scan.impact > 2.5) {
        return "is likely to be described as having a relatively high impact.";
      }
      if (this.scan.impact > 0) {
        return "has elements of both subtlety and impact. Depending on your goals you may wish to revise this";
      }
    },
    explainSimplicity() {
      if (this.scan.simplicity < -4) {
        return "presents a large amount of information. There is a lot for the user to look at.";
      }
      if (this.scan.simplicity < -2.5) {
        return "has a reasonable amount of information presented but without being overwhelming.";
      }
      if (this.scan.simplicity < 0) {
        return "presents a volume of information but without being too 'busy'. Depending on your goals you may wish to revise this";
      }
      if (this.scan.simplicity > 4) {
        return "is extremely simple and is likely to be described as minimalist.";
      }
      if (this.scan.simplicity > 2.5) {
        return "is fairly simple presenting the user with few choices.";
      }
      if (this.scan.simplicity > 0) {
        return "has a degree of simplicity yet still provides information. You may want to amend the design to move more towards one or the other";
      }
    },
    modifyAndRound(num) {
      let value = Math.round((num + 5) * 10);
      if (value < 0) {
        value = 0;
      }
      return value;
    },
    niceScore(scoreType) {
      let score = 0;

      switch (scoreType) {
        case "flow":
          score = this.modifyAndRound(this.scan.flow * 1);
          break;
        case "impact":
          score = this.modifyAndRound(this.scan.impact * 1);
          break;
        case "simplicity":
          score = this.modifyAndRound(this.scan.simplicity * 1);
          break;
        case "balance":
          score = 100 - this.modifyAndRound(this.scan.flow * 1);
          break;
        case "subtlety":
          score = 100 - this.modifyAndRound(this.scan.impact * 1);
          break;
        case "information":
          score = 100 - this.modifyAndRound(this.scan.simplicity * 1);
          break;
      }
      return score;
    },
    flowSections() {
      return [
        { label: "Flow", value: this.niceScore("flow"), color: "#0B7A75" },
        {
          label: "Balance",
          value: this.niceScore("balance"),
          color: "#49DCB1",
        },
      ];
    },
    impactSections() {
      return [
        { label: "Impact", value: this.niceScore("impact"), color: "#0B7A75" },
        {
          label: "Subtlety",
          value: this.niceScore("subtlety"),
          color: "#49DCB1",
        },
      ];
    },
    simplicitySections() {
      return [
        {
          label: "Simplicity",
          value: this.niceScore("simplicity"),
          color: "#0B7A75",
        },
        {
          label: "Information",
          value: this.niceScore("information"),
          color: "#49DCB1",
        },
      ];
    },
    async link() {
      let identityId = await Auth.currentAuthenticatedUser({
        bypassCache: true,
      });
      let user = identityId.username;
      let key = this.scan.filename.split("/").pop();
      let config = {
        level: "private",
        identityId: user,
      };
      let link = await Storage.get(key, config);
      return link;
    },
    refreshPage() {
      location.reload();
    },
  },
  created() {
    this.fetchItem();
    this.fetchData();
    Analytics.record({ name: "viewedScan" });
  },
};
</script>

<style lang="scss">
#explanation {
  p {
    @apply mb-4;
  }
  ol,
  ul {
    @apply list-disc pl-5 mb-4;
  }
  h2,
  h3 {
    @apply mt-4 mb-2 text-lg font-bold;
  }
}
</style>
